/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { ChevronDown, Plus, Minus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const FAQItem: React.FC<{ question: string, answer: string }> = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className={`transition-all duration-300 border border-stone-200 dark:border-white/5 rounded-card mb-4 overflow-hidden ${isOpen ? 'bg-stone-50 dark:bg-white/[0.03] border-nobel-gold/20' : 'bg-white dark:bg-white/[0.01] hover:border-stone-300 dark:hover:border-white/10'}`}>
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-6 py-6 flex justify-between items-center text-left focus:outline-none group"
            >
                <span className="font-serif text-lg md:text-xl text-[#0A0B0F] dark:text-white transition-colors pr-8 leading-tight font-bold">
                    {question}
                </span>
                <div className={`p-2 rounded-lg transition-colors ${isOpen ? 'bg-nobel-gold text-white' : 'bg-stone-100 dark:bg-white/5 text-stone-400 group-hover:text-stone-900 dark:group-hover:text-white'}`}>
                    {isOpen ? <Minus size={18} strokeWidth={3} /> : <Plus size={18} strokeWidth={3} />}
                </div>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div 
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        <div className="px-6 pb-8 text-stone-600 dark:text-stone-400 leading-relaxed text-sm md:text-base max-w-4xl border-t border-stone-100 dark:border-white/5 pt-6">
                            {answer}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export const FAQ: React.FC = () => {
  const faqs = [
      {
          question: "Is GhostOS strictly non-custodial?",
          answer: "Yes. We fundamentally believe in 'Not your keys, not your coins'. GhostOS generates your private keys locally on your device or via hardware secure enclave. We never have access to your funds, nor can we freeze your account at the protocol level."
      },
      {
          question: "How does instant settlement work?",
          answer: "We utilize the high-throughput capabilities of Solana and Sui networks. When a customer pays via the POS, the funds are settled on-chain in sub-seconds. You can choose to auto-convert these to USDC instantly to avoid volatility."
      },
      {
          question: "What makes the 'Escrow' smart?",
          answer: "Our escrow contracts are programmatic agreements deployed on-chain. Funds are locked in the contract—not held by us. They are only released when pre-defined conditions (milestones) are cryptographically signed by both parties."
      },
      {
          question: "What are the transaction fees?",
          answer: "GhostOS charges a flat 0.1% fee on POS and Escrow transactions for standard users. Premium and Sovereign members enjoy 0% transaction fees. This is in addition to the blockchain's network fee, which is typically a fraction of a cent."
      },
      {
          question: "How is my data kept private?",
          answer: "All transaction metadata is encrypted end-to-end. For maximum privacy, our Escrow contracts support stealth addresses, which are single-use, unlinkable addresses that protect your financial history from public analysis."
      },
      {
          question: "Which blockchains and tokens are supported?",
          answer: "We currently offer native support for the Solana and Sui blockchains. You can transact with native tokens like SOL and SUI, as well as major stablecoins like USDC and other ecosystem tokens. wGHOST wrapping is available for cross-chain liquidity."
      }
  ];

  return (
    <section id="faq" className="py-32 bg-white dark:bg-[#0A0B0F]">
      <div className="container mx-auto px-6">
         <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="font-serif text-4xl md:text-5xl text-[#0A0B0F] dark:text-white mb-6 font-bold">Common Questions</h2>
            <p className="text-stone-500 dark:text-stone-400 text-lg italic">The essential guide to operating on the Ghost Network.</p>
         </div>
         <div className="max-w-4xl mx-auto">
             {faqs.map((f, i) => (
                 <FAQItem key={i} question={f.question} answer={f.answer} />
             ))}
         </div>
      </div>
    </section>
  );
};